import { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import toast from 'react-hot-toast';
import {
	ArrowLeft,
	Check,
	FileText,
	Loader2,
	Pencil,
	Trash2,
	Upload,
	X,
} from 'lucide-react';
import { Spinner } from '../../shared/ui/spinner';
import AddRecordModal from '../../components/AddRecordModal/AddRecordModal';
import { expensesGerCategoriesRequest } from '../Expenses/api';
import { incomesGerCategoriesRequest } from '../Income/api';
import {
	cancelStatementImport,
	commitStatementImport,
	deleteStatementTransaction,
	getStatementImport,
	listStatementImports,
	updateStatementTransaction,
	uploadStatement,
} from './api';
import styles from './StatementImports.module.css';

const ALLOWED_EXTENSIONS = ['.pdf', '.xls', '.xlsx'];
const MAX_FILE_SIZE = 10 * 1024 * 1024;

function getErrorMessage(error, fallback) {
	return error?.response?.data?.detail || error?.message || fallback;
}

function formatDate(value) {
	if (!value) return '—';
	try {
		return new Date(value).toLocaleDateString();
	} catch {
		return value;
	}
}

function skippableDuplicateCount(transactions) {
	return (transactions || []).filter(tx => tx.is_duplicate && !tx.keep_anyway)
		.length;
}

export const StatementImports = () => {
	const intl = useIntl();
	const fileInputRef = useRef(null);

	const [view, setView] = useState('home'); // home | review
	const [loadingList, setLoadingList] = useState(true);
	const [uploading, setUploading] = useState(false);
	const [loadingImport, setLoadingImport] = useState(false);
	const [saving, setSaving] = useState(false);

	const [drafts, setDrafts] = useState([]);
	const [selectedFile, setSelectedFile] = useState(null);
	const [outputLanguage, setOutputLanguage] = useState('original');
	const [activeImport, setActiveImport] = useState(null);

	const [expenseCategories, setExpenseCategories] = useState([]);
	const [incomeCategories, setIncomeCategories] = useState([]);

	const [editingTx, setEditingTx] = useState(null);
	const [confirmAction, setConfirmAction] = useState(null); // commit | cancel | deleteTx
	const [pendingDeleteTxId, setPendingDeleteTxId] = useState(null);

	const loadDrafts = useCallback(async () => {
		try {
			setLoadingList(true);
			const data = await listStatementImports('pending');
			setDrafts(data?.imports || []);
		} catch (error) {
			toast.error(
				getErrorMessage(
					error,
					intl.formatMessage({ id: 'StatementImportIDErrorLoad' }),
				),
			);
		} finally {
			setLoadingList(false);
		}
	}, [intl]);

	const loadCategories = useCallback(async () => {
		try {
			const [expenseData, incomeData] = await Promise.all([
				expensesGerCategoriesRequest(),
				incomesGerCategoriesRequest(),
			]);
			setExpenseCategories(
				expenseData?.categories ||
					expenseData?.data ||
					(Array.isArray(expenseData) ? expenseData : []) ||
					[],
			);
			setIncomeCategories(
				incomeData?.categories ||
					incomeData?.data ||
					(Array.isArray(incomeData) ? incomeData : []) ||
					[],
			);
		} catch (error) {
			console.error('Failed to load categories', error);
		}
	}, []);

	useEffect(() => {
		loadDrafts();
		loadCategories();
	}, [loadDrafts, loadCategories]);

	const openImport = async importId => {
		try {
			setLoadingImport(true);
			const data = await getStatementImport(importId);
			setActiveImport(data);
			setView('review');
		} catch (error) {
			toast.error(
				getErrorMessage(
					error,
					intl.formatMessage({ id: 'StatementImportIDErrorLoad' }),
				),
			);
		} finally {
			setLoadingImport(false);
		}
	};

	const handleFileChange = event => {
		const file = event.target.files?.[0];
		if (!file) return;

		const ext = `.${file.name.split('.').pop()?.toLowerCase()}`;
		if (!ALLOWED_EXTENSIONS.includes(ext)) {
			toast.error(intl.formatMessage({ id: 'StatementImportIDInvalidFile' }));
			event.target.value = '';
			return;
		}
		if (file.size > MAX_FILE_SIZE) {
			toast.error(intl.formatMessage({ id: 'StatementImportIDFileTooLarge' }));
			event.target.value = '';
			return;
		}
		setSelectedFile(file);
	};

	const handleUpload = async () => {
		if (!selectedFile) {
			toast.error(intl.formatMessage({ id: 'StatementImportIDSelectFile' }));
			return;
		}
		try {
			setUploading(true);
			const data = await uploadStatement(selectedFile, outputLanguage);
			setSelectedFile(null);
			if (fileInputRef.current) fileInputRef.current.value = '';
			setActiveImport(data);
			setView('review');
			toast.success(
				intl.formatMessage({ id: 'StatementImportIDUploadSuccess' }),
			);
			await loadDrafts();
		} catch (error) {
			toast.error(
				getErrorMessage(
					error,
					intl.formatMessage({ id: 'StatementImportIDErrorUpload' }),
				),
			);
		} finally {
			setUploading(false);
		}
	};

	const categoryOptionsForType = type =>
		type === 'income' ? incomeCategories : expenseCategories;

	const categoryName = (type, categoryId) => {
		const list = categoryOptionsForType(type);
		const found = list.find(c => String(c.id) === String(categoryId));
		return found?.name || found?.original_name || '—';
	};

	const startEdit = tx => {
		setEditingTx(tx);
	};

	const handleDraftSave = async recordData => {
		if (!activeImport || !editingTx) return;
		const payload = {
			type: editingTx.type,
			amount: recordData.amount,
			currency: recordData.currency,
			date: recordData.date,
			description: recordData.description || null,
			category_id: recordData.category_id,
		};
		if (editingTx.type === 'expense') {
			payload.payment_method = recordData.payment_method || 'cash';
			payload.source_id = null;
		} else {
			payload.payment_method = null;
			payload.source_id = recordData.source_id || null;
		}
		await updateStatementTransaction(activeImport.id, editingTx.id, payload);
	};

	const handleEditSuccess = async () => {
		if (!activeImport) return;
		try {
			const refreshed = await getStatementImport(activeImport.id);
			setActiveImport(refreshed);
			toast.success(
				intl.formatMessage({ id: 'StatementImportIDUpdateSuccess' }),
			);
		} catch (error) {
			toast.error(
				getErrorMessage(
					error,
					intl.formatMessage({ id: 'StatementImportIDErrorUpdate' }),
				),
			);
		} finally {
			setEditingTx(null);
		}
	};

	const handleKeepAnyway = async (tx, keepAnyway) => {
		if (!activeImport) return;
		try {
			setSaving(true);
			await updateStatementTransaction(activeImport.id, tx.id, {
				keep_anyway: keepAnyway,
			});
			const refreshed = await getStatementImport(activeImport.id);
			setActiveImport(refreshed);
			toast.success(
				intl.formatMessage({
					id: keepAnyway
						? 'StatementImportIDKeepSuccess'
						: 'StatementImportIDSkipSuccess',
				}),
			);
		} catch (error) {
			toast.error(
				getErrorMessage(
					error,
					intl.formatMessage({ id: 'StatementImportIDErrorUpdate' }),
				),
			);
		} finally {
			setSaving(false);
		}
	};

	const confirmDeleteTx = txId => {
		setPendingDeleteTxId(txId);
		setConfirmAction('deleteTx');
	};

	const runConfirmAction = async () => {
		if (!activeImport) return;
		try {
			setSaving(true);
			if (confirmAction === 'deleteTx' && pendingDeleteTxId) {
				await deleteStatementTransaction(activeImport.id, pendingDeleteTxId);
				const refreshed = await getStatementImport(activeImport.id);
				setActiveImport(refreshed);
				toast.success(
					intl.formatMessage({ id: 'StatementImportIDDeleteTxSuccess' }),
				);
			} else if (confirmAction === 'commit') {
				const result = await commitStatementImport(activeImport.id);
				const skipped = result.duplicates_skipped || 0;
				if (skipped > 0) {
					toast.success(
						intl.formatMessage(
							{ id: 'StatementImportIDCommitSuccessWithSkipped' },
							{
								expenses: result.expenses_created,
								incomes: result.incomes_created,
								skipped,
							},
						),
					);
				} else {
					toast.success(
						intl.formatMessage(
							{ id: 'StatementImportIDCommitSuccess' },
							{
								expenses: result.expenses_created,
								incomes: result.incomes_created,
							},
						),
					);
				}
				setActiveImport(null);
				setView('home');
				await loadDrafts();
			} else if (confirmAction === 'cancel') {
				await cancelStatementImport(activeImport.id);
				toast.success(
					intl.formatMessage({ id: 'StatementImportIDCancelSuccess' }),
				);
				setActiveImport(null);
				setView('home');
				await loadDrafts();
			}
		} catch (error) {
			toast.error(
				getErrorMessage(
					error,
					intl.formatMessage({ id: 'StatementImportIDErrorAction' }),
				),
			);
		} finally {
			setSaving(false);
			setConfirmAction(null);
			setPendingDeleteTxId(null);
		}
	};

	const backToHome = () => {
		setView('home');
		setActiveImport(null);
		setEditingTx(null);
		loadDrafts();
	};

	const renderDuplicateMeta = tx => {
		if (!tx.is_duplicate) return null;
		const match = tx.duplicate_of;
		const hint = match
			? intl.formatMessage(
					{ id: 'StatementImportIDDuplicateHint' },
					{
						description: match.description || '—',
						amount: Number(match.amount).toLocaleString(),
						currency: match.currency,
						date: match.date,
					},
				)
			: null;
		return (
			<div className={styles.duplicateMeta}>
				<span
					className={
						tx.keep_anyway ? styles.duplicateKept : styles.duplicateBadge
					}
				>
					{tx.keep_anyway
						? intl.formatMessage({ id: 'StatementImportIDDuplicateKept' })
						: intl.formatMessage({ id: 'StatementImportIDDuplicateBadge' })}
				</span>
				{hint ? <span className={styles.duplicateHint}>{hint}</span> : null}
				<button
					type='button'
					className={styles.keepAnywayButton}
					onClick={() => handleKeepAnyway(tx, !tx.keep_anyway)}
					disabled={saving}
				>
					{tx.keep_anyway
						? intl.formatMessage({ id: 'StatementImportIDUndoKeep' })
						: intl.formatMessage({ id: 'StatementImportIDKeepAnyway' })}
				</button>
			</div>
		);
	};

	const skippableDuplicates = skippableDuplicateCount(
		activeImport?.transactions,
	);

	if (loadingImport) {
		return (
			<div className={styles.container}>
				<Spinner />
			</div>
		);
	}

	return (
		<div className={styles.container}>
			<div className={styles.content}>
				{view === 'home' && (
					<>
						<div className={styles.pageHeader}>
							<div>
								<h1 className={styles.title}>
									{intl.formatMessage({ id: 'StatementImportIDTitle' })}
								</h1>
								<p className={styles.subtitle}>
									{intl.formatMessage({ id: 'StatementImportIDSubtitle' })}
								</p>
							</div>
						</div>

						<div className={styles.panels}>
							<section className={styles.card}>
								<div className={styles.cardHeader}>
									<Upload size={20} color='#34a574' />
									<h2 className={styles.cardTitle}>
										{intl.formatMessage({ id: 'StatementImportIDUploadTitle' })}
									</h2>
								</div>
								<p className={styles.cardHint}>
									{intl.formatMessage({ id: 'StatementImportIDUploadHint' })}
								</p>

								<div className={styles.uploadArea}>
									<input
										ref={fileInputRef}
										type='file'
										accept='.pdf,.xls,.xlsx,application/pdf,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
										onChange={handleFileChange}
										className={styles.fileInput}
										id='statement-file-input'
									/>
									<label
										htmlFor='statement-file-input'
										className={styles.fileLabel}
									>
										<FileText size={28} color='#4cc490' />
										<span>
											{selectedFile
												? selectedFile.name
												: intl.formatMessage({
														id: 'StatementImportIDChooseFile',
													})}
										</span>
									</label>
								</div>

								<div className={styles.fieldGroup}>
									<label className={styles.label}>
										{intl.formatMessage({ id: 'StatementImportIDLanguage' })}
									</label>
									<select
										className={styles.select}
										value={outputLanguage}
										onChange={e => setOutputLanguage(e.target.value)}
									>
										<option value='original'>
											{intl.formatMessage({
												id: 'StatementImportIDLanguageOriginal',
											})}
										</option>
										<option value='en'>
											{intl.formatMessage({
												id: 'StatementImportIDLanguageEnglish',
											})}
										</option>
									</select>
								</div>

								<button
									type='button'
									className={styles.primaryButton}
									onClick={handleUpload}
									disabled={uploading || !selectedFile}
								>
									{uploading ? (
										<>
											<Loader2 size={18} className={styles.spin} />
											{intl.formatMessage({ id: 'StatementImportIDAnalyzing' })}
										</>
									) : (
										<>
											<Upload size={18} />
											{intl.formatMessage({ id: 'StatementImportIDUploadCta' })}
										</>
									)}
								</button>
							</section>

							<section className={styles.card}>
								<div className={styles.cardHeader}>
									<FileText size={20} color='#34a574' />
									<h2 className={styles.cardTitle}>
										{intl.formatMessage({ id: 'StatementImportIDDraftsTitle' })}
									</h2>
								</div>
								<p className={styles.cardHint}>
									{intl.formatMessage({ id: 'StatementImportIDDraftsHint' })}
								</p>

								{loadingList ? (
									<div className={styles.inlineLoading}>
										<Spinner />
									</div>
								) : drafts.length === 0 ? (
									<div className={styles.emptyState}>
										{intl.formatMessage({ id: 'StatementImportIDNoDrafts' })}
									</div>
								) : (
									<div className={styles.draftList}>
										{drafts.map(item => (
											<button
												key={item.id}
												type='button'
												className={styles.draftItem}
												onClick={() => openImport(item.id)}
											>
												<div className={styles.draftInfo}>
													<span className={styles.draftName}>
														{item.original_filename}
													</span>
													<span className={styles.draftMeta}>
														{intl.formatMessage(
															{ id: 'StatementImportIDDraftMeta' },
															{
																count: item.transaction_count,
																date: formatDate(item.created_at),
															},
														)}
													</span>
												</div>
												<span className={styles.draftBadge}>
													{intl.formatMessage({
														id: 'StatementImportIDStatusPending',
													})}
												</span>
											</button>
										))}
									</div>
								)}
							</section>
						</div>
					</>
				)}

				{view === 'review' && activeImport && (
					<>
						<div className={styles.pageHeader}>
							<div className={styles.reviewTitleRow}>
								<button
									type='button'
									className={styles.backButton}
									onClick={backToHome}
								>
									<ArrowLeft size={18} />
									{intl.formatMessage({ id: 'StatementImportIDBack' })}
								</button>
								<div>
									<h1 className={styles.title}>
										{activeImport.original_filename}
									</h1>
									<p className={styles.subtitle}>
										{skippableDuplicates > 0
											? intl.formatMessage(
													{ id: 'StatementImportIDReviewSubtitleDuplicates' },
													{
														count: activeImport.transactions?.length || 0,
														duplicates: skippableDuplicates,
													},
												)
											: intl.formatMessage(
													{ id: 'StatementImportIDReviewSubtitle' },
													{ count: activeImport.transactions?.length || 0 },
												)}
									</p>
								</div>
							</div>
							<div className={styles.headerActions}>
								<button
									type='button'
									className={styles.secondaryButton}
									onClick={() => setConfirmAction('cancel')}
									disabled={saving}
								>
									<X size={16} />
									{intl.formatMessage({ id: 'StatementImportIDCancel' })}
								</button>
								<button
									type='button'
									className={styles.primaryButton}
									onClick={() => setConfirmAction('commit')}
									disabled={saving || !(activeImport.transactions?.length > 0)}
								>
									<Check size={16} />
									{intl.formatMessage({ id: 'StatementImportIDCommit' })}
								</button>
							</div>
						</div>

						<div className={styles.tableContainer}>
							<div className={styles.tableHeader}>
								<div className={styles.tableHeaderCell}>
									{intl.formatMessage({ id: 'StatementImportIDColType' })}
								</div>
								<div className={styles.tableHeaderCell}>
									{intl.formatMessage({ id: 'StatementImportIDColDate' })}
								</div>
								<div className={styles.tableHeaderCell}>
									{intl.formatMessage({
										id: 'StatementImportIDColDescription',
									})}
								</div>
								<div className={styles.tableHeaderCell}>
									{intl.formatMessage({ id: 'StatementImportIDColCategory' })}
								</div>
								<div className={styles.tableHeaderCell}>
									{intl.formatMessage({ id: 'StatementImportIDColAmount' })}
								</div>
								<div className={styles.tableHeaderCell}>
									{intl.formatMessage({ id: 'StatementImportIDColActions' })}
								</div>
							</div>
							<div className={styles.tableBody}>
								{(activeImport.transactions || []).length === 0 ? (
									<div className={styles.emptyState}>
										{intl.formatMessage({ id: 'StatementImportIDEmptyTx' })}
									</div>
								) : (
									activeImport.transactions.map(tx => (
										<div
											key={tx.id}
											className={`${styles.tableRow} ${
												tx.is_duplicate
													? tx.keep_anyway
														? styles.tableRowKept
														: styles.tableRowDuplicate
													: ''
											}`}
										>
											<div className={styles.tableCell}>
												<span
													className={
														tx.type === 'income'
															? styles.typeIncome
															: styles.typeExpense
													}
												>
													{tx.type === 'income'
														? intl.formatMessage({
																id: 'StatementImportIDTypeIncome',
															})
														: intl.formatMessage({
																id: 'StatementImportIDTypeExpense',
															})}
												</span>
											</div>
											<div className={styles.tableCell}>{tx.date}</div>
											<div className={styles.tableCell}>
												<div className={styles.descriptionStack}>
													<span>{tx.description || '—'}</span>
													{renderDuplicateMeta(tx)}
												</div>
											</div>
											<div className={styles.tableCell}>
												{categoryName(tx.type, tx.category_id)}
											</div>
											<div className={styles.tableCell}>
												{Number(tx.amount).toLocaleString()} {tx.currency}
											</div>
											<div className={styles.tableCellActions}>
												<button
													type='button'
													className={styles.iconButton}
													onClick={() => startEdit(tx)}
													aria-label='Edit'
												>
													<Pencil size={16} />
												</button>
												<button
													type='button'
													className={styles.iconButtonDanger}
													onClick={() => confirmDeleteTx(tx.id)}
													aria-label='Delete'
												>
													<Trash2 size={16} />
												</button>
											</div>
										</div>
									))
								)}
							</div>
						</div>

						<div className={styles.cardsContainer}>
							{(activeImport.transactions || []).map(tx => (
								<div
									key={tx.id}
									className={`${styles.txCard} ${
										tx.is_duplicate
											? tx.keep_anyway
												? styles.txCardKept
												: styles.txCardDuplicate
											: ''
									}`}
								>
									<div className={styles.cardTopRow}>
										<span
											className={
												tx.type === 'income'
													? styles.typeIncome
													: styles.typeExpense
											}
										>
											{tx.type === 'income'
												? intl.formatMessage({
														id: 'StatementImportIDTypeIncome',
													})
												: intl.formatMessage({
														id: 'StatementImportIDTypeExpense',
													})}
										</span>
										<span className={styles.cardDate}>{tx.date}</span>
									</div>
									<div className={styles.cardDescription}>
										{tx.description || '—'}
									</div>
									{renderDuplicateMeta(tx)}
									<div className={styles.cardMainRow}>
										<span>{categoryName(tx.type, tx.category_id)}</span>
										<strong>
											{Number(tx.amount).toLocaleString()} {tx.currency}
										</strong>
									</div>
									<div className={styles.cardActions}>
										<button
											type='button'
											className={styles.secondaryButton}
											onClick={() => startEdit(tx)}
										>
											<Pencil size={14} />
											{intl.formatMessage({ id: 'StatementImportIDEdit' })}
										</button>
										<button
											type='button'
											className={styles.dangerButton}
											onClick={() => confirmDeleteTx(tx.id)}
										>
											<Trash2 size={14} />
											{intl.formatMessage({ id: 'StatementImportIDDelete' })}
										</button>
									</div>
								</div>
							))}
						</div>
					</>
				)}
			</div>

			<AddRecordModal
				open={Boolean(editingTx)}
				onClose={() => setEditingTx(null)}
				onSuccess={handleEditSuccess}
				defaultTab={editingTx?.type === 'income' ? 'income' : 'expense'}
				editingExpense={
					editingTx?.type === 'expense'
						? {
								id: editingTx.id,
								amount: editingTx.amount,
								currency: editingTx.currency,
								category_id: editingTx.category_id,
								date: editingTx.date,
								description: editingTx.description || '',
								payment_method: editingTx.payment_method || 'cash',
							}
						: null
				}
				editingIncome={
					editingTx?.type === 'income'
						? {
								id: editingTx.id,
								amount: editingTx.amount,
								currency: editingTx.currency,
								category_id: editingTx.category_id,
								date: editingTx.date,
								description: editingTx.description || '',
								source_id: editingTx.source_id || null,
							}
						: null
				}
				onCustomSave={handleDraftSave}
			/>

			{confirmAction && (
				<div className={styles.modalOverlay}>
					<div className={styles.modal}>
						<h3 className={styles.modalTitle}>
							{confirmAction === 'commit'
								? skippableDuplicates > 0
									? intl.formatMessage(
											{ id: 'StatementImportIDConfirmCommitWithDuplicates' },
											{ count: skippableDuplicates },
										)
									: intl.formatMessage({ id: 'StatementImportIDConfirmCommit' })
								: confirmAction === 'cancel'
									? intl.formatMessage({ id: 'StatementImportIDConfirmCancel' })
									: intl.formatMessage({
											id: 'StatementImportIDConfirmDeleteTx',
										})}
						</h3>
						<div className={styles.modalActions}>
							<button
								type='button'
								className={styles.secondaryButton}
								onClick={() => {
									setConfirmAction(null);
									setPendingDeleteTxId(null);
								}}
								disabled={saving}
							>
								{intl.formatMessage({ id: 'StatementImportIDClose' })}
							</button>
							<button
								type='button'
								className={
									confirmAction === 'commit'
										? styles.primaryButton
										: styles.dangerButton
								}
								onClick={runConfirmAction}
								disabled={saving}
							>
								{saving
									? intl.formatMessage({ id: 'StatementImportIDSaving' })
									: intl.formatMessage({ id: 'StatementImportIDConfirm' })}
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default StatementImports;
